import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import WhatsappIntegrationModal from '../../components/WhatsappIntegrationModal.jsx'
import MetaIntegrationModal from '../../components/MetaIntegrationModal.jsx'
import MetaLeadAdsModal from '../../components/MetaLeadAdsModal.jsx'
import ConnectionManageModal from '../../components/ConnectionManageModal.jsx'
import MetaMessagingModal from '../../components/MetaMessagingModal.jsx'
import {
  PRODUCT_INFO, buildMetaCards, connectedBy, connectionStatus, productOf, scopesOf,
} from '../../lib/metaConnections.js'
import {
  deleteConfirmation, googlePickerState, manageActions, metaPickerState,
} from '../../lib/connectionManage.js'
import { clientHint, disconnectImpact, isUnlinked, visibleConnections } from '../../lib/connectionScope.js'
import TrackerInstallModal from '../../components/TrackerInstallModal.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import { useAccess } from '../../hooks/useAccess.js'

/**
 * Conexões — lista APENAS as integrações já configuradas num grid de cards
 * (Google Ads / Meta Ads a nível de organização; WhatsApp Evolution, WhatsApp
 * Oficial e Tracker do cliente selecionado no topo). Integrações não
 * configuradas não ocupam espaço — são adicionadas pelo botão "+ Criar", que
 * abre um seletor por categoria.
 *
 * Nenhuma regra de negócio muda: as ações reaproveitam os mesmos endpoints e
 * modais existentes.
 */
export default function ConfigConexoes() {
  const { toast, availableClients, selectedClient, accessMode } = useApp()
  const navigate = useNavigate()
  const { isOrgAdmin } = useAccess()
  const allClients = useMemo(() => availableClients || [], [availableClients])
  const canManageClients = accessMode !== 'client_only'

  const [googleConns, setGoogleConns] = useState([])
  const [metaConns, setMetaConns] = useState([])
  const [waStatus, setWaStatus] = useState({})
  const [waChecking, setWaChecking] = useState(false)
  const [metaByClient, setMetaByClient] = useState({})
  const [loading, setLoading] = useState(true)
  const [authorizing, setAuthorizing] = useState(false)
  const [query, setQuery] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  // Modais por cliente (reaproveitados de Clientes).
  const [waClient, setWaClient] = useState(null)
  const [metaClient, setMetaClient] = useState(null)
  const [trackerClient, setTrackerClient] = useState(null)
  const [leadAdsOpen, setLeadAdsOpen] = useState(false)
  const [messagingOpen, setMessagingOpen] = useState(false)
  // Gerenciar uma conexão (#355): { kind: 'google' | 'gcal' | 'meta', id }. O conteúdo é derivado das listas
  // carregadas, então o modal acompanha a conexão depois de recarregar (e fecha se ela sumir).
  const [manage, setManage] = useState(null)
  const [manageBusy, setManageBusy] = useState(false)

  const loadGoogle = useCallback(async () => {
    try {
      const data = await api.getConnections()
      setGoogleConns(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('[connections:google] erro:', err)
    }
  }, [])

  const loadMeta = useCallback(async () => {
    try {
      const data = await api.getMetaConnections()
      setMetaConns(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('[connections:meta] erro:', err)
    }
  }, [])

  const loadWaStatus = useCallback(async (refresh = false) => {
    if (!canManageClients) return
    try {
      const instances = await api.listWhatsappInstances({ refresh })
      const map = {}
      instances.forEach((i) => { map[i.client_id] = i.status })
      setWaStatus(map)
    } catch {
      /* silencioso — status é informativo */
    }
  }, [canManageClients])

  // Revalida ao vivo na Evolution (refresh=true) sem travar o card: o status
  // do banco já está na tela, isto só atualiza o badge quando a resposta chegar.
  const refreshWaLive = useCallback(() => {
    setWaChecking(true)
    return loadWaStatus(true).finally(() => setWaChecking(false))
  }, [loadWaStatus])

  const loadMetaAccounts = useCallback(async () => {
    if (!canManageClients) return
    try {
      const accounts = await api.listWhatsappMetaAccounts()
      const map = {}
      accounts.forEach((a) => {
        const cur = map[a.client_id]
        if (!cur || (cur.status !== 'active' && a.status === 'active')) {
          map[a.client_id] = a
        }
      })
      setMetaByClient(map)
    } catch {
      /* silencioso — status é informativo */
    }
  }, [canManageClients])

  useEffect(() => {
    let alive = true
    // WhatsApp entra com o status do banco (rápido, refresh=false) pra não
    // segurar o card atrás da latência da Evolution — a checagem ao vivo roda
    // depois, em segundo plano, e só atualiza o badge quando responder.
    Promise.all([loadGoogle(), loadMeta(), loadMetaAccounts()])
      .finally(() => { if (alive) setLoading(false) })
    loadWaStatus(false).then(() => { if (alive) refreshWaLive() })
    // Reconciliação ao vivo periódica (não só no mount): se o webhook da
    // Evolution não chegar, o polling seguinte corrige o badge sozinho
    // (issue #81). refresh=true já é 1 chamada em paralelo por instância
    // (issue #80) — não reintroduz a lentidão original.
    const t = setInterval(() => loadWaStatus(true), 20_000)
    return () => { alive = false; clearInterval(t) }
  }, [loadGoogle, loadMeta, loadWaStatus, loadMetaAccounts, refreshWaLive])

  async function connectGoogleAds() {
    try {
      setAuthorizing(true)
      const { authorize_url: url } = await api.startGoogleAdsOAuth()
      if (!url) throw new Error('URL de autorização vazia.')
      window.location.assign(url)
    } catch (err) {
      setAuthorizing(false)
      toast(err.message || 'Falha ao iniciar autorização Google.', 'error')
    }
  }

  async function connectMeta(product = 'ads') {
    try {
      setAuthorizing(true)
      const { authorize_url: url } = await api.startMetaAdsOAuth({ product })
      if (!url) throw new Error('URL de autorização vazia.')
      window.location.assign(url)
    } catch (err) {
      setAuthorizing(false)
      toast(err.message || 'Falha ao iniciar autorização Meta.', 'error')
    }
  }

  async function enableMetaManagement() {
    try {
      setAuthorizing(true)
      const { authorize_url: url } = await api.startMetaAdsOAuth({ management: true, product: 'ads' })
      if (!url) throw new Error('URL de autorização vazia.')
      window.location.assign(url)
    } catch (err) {
      setAuthorizing(false)
      toast(err.message || 'Falha ao iniciar autorização Meta.', 'error')
    }
  }

  // ── Gerenciar conexão (#355) ───────────────────────────────────────────────
  // Conteúdo do modal, derivado das listas carregadas.
  const managed = useMemo(() => {
    if (!manage) return null
    const conn = (manage.kind === 'meta' ? metaConns : googleConns).find((c) => c.id === manage.id)
    if (!conn) return null
    const served = Object.entries(conn.client_accounts || {})
      .filter(([, n]) => Number(n) > 0)
      .map(([id, n]) => ({
        id, name: allClients.find((c) => c.id === id)?.name || 'Cliente removido',
        label: `${n} ${Number(n) === 1 ? 'conta' : 'contas'}`,
      }))
    if (manage.kind === 'meta') {
      const product = productOf(conn)
      const status = connectionStatus(conn)
      return {
        kind: 'meta', product, conn, served, title: PRODUCT_INFO[product].label, subtitle: connectedBy(conn),
        statusLabel: status.label, tone: status.tone,
        actions: manageActions({
          kind: 'meta', product, active: conn.is_active,
          managementEnabled: scopesOf(conn).includes('ads_management'), canManageAds: isOrgAdmin,
        }),
      }
    }
    const ok = conn.is_active && !conn.last_error
    return {
      kind: manage.kind, conn, served, title: manage.kind === 'gcal' ? 'Google Calendar' : 'Google Ads',
      subtitle: conn.google_email, statusLabel: ok ? 'Conectado' : 'Reautorizar', tone: ok ? 'green' : 'amber',
      actions: manageActions({ kind: manage.kind, active: conn.is_active }),
    }
  }, [manage, googleConns, metaConns, allClients, isOrgAdmin])

  // Nada mais a mostrar (a conexão foi excluída): fecha o modal.
  useEffect(() => {
    if (manage && !managed && !loading) setManage(null)
  }, [manage, managed, loading])

  async function linkAccounts(target) {
    const { conn, kind } = target
    if (kind === 'google') {
      const res = await api.listGoogleAdsConnectionAccounts(conn.id)
      navigate('/integrations/google-ads/contas', { state: googlePickerState(res) })
    } else {
      const res = await api.listMetaAdsConnectionAccounts(conn.id)
      navigate('/integrations/meta-ads/contas', { state: metaPickerState(res) })
    }
  }

  async function deleteConnection(target) {
    const { conn, kind } = target
    if (!window.confirm(deleteConfirmation(target.title, disconnectImpact(conn, allClients)))) return
    if (kind === 'google') await api.deleteGoogleAdsConnection(conn.id)
    else await api.deleteMetaConnection(conn.id)
    toast('Conexão excluída.', 'success')
    setManage(null)
    await Promise.all([loadGoogle(), loadMeta()])
  }

  async function onManageAction(id) {
    const target = managed
    if (!target) return
    if (id === 'reconnect') return target.kind === 'google' ? connectGoogleAds() : connectMeta(target.product)
    if (id === 'enable-management') return enableMetaManagement()
    if (id === 'open-agenda') return navigate('/agenda')
    if (id === 'lead-ads') { setManage(null); setLeadAdsOpen(true); return }
    if (id === 'messaging') { setManage(null); setMessagingOpen(true); return }
    setManageBusy(true)
    try {
      if (id === 'link-accounts') await linkAccounts(target)
      else if (id === 'delete') await deleteConnection(target)
    } catch (err) {
      toast(err.message || 'Não foi possível concluir a ação.', 'error')
    } finally {
      setManageBusy(false)
    }
  }

  // ── Cards: SOMENTE integrações já configuradas ─────────────────────────────
  const cards = useMemo(() => {
    const list = []

    // Conexões OAuth do Google (org). A mesma tabela guarda Ads e Calendar
    // (issue #72), então o card vem do `provider` — sem isso um calendário
    // conectado apareceria como "Google Ads" e o Desconectar chamaria o
    // endpoint errado. Conexão antiga sem provider é Ads, o único que existia.
    const clientId = selectedClient?.id || null
    visibleConnections(googleConns, clientId).forEach((c) => {
      const ok = c.is_active && !c.last_error
      const unlinked = isUnlinked(c)
      if ((c.provider || 'google_ads') === 'google_calendar') {
        // Desconectar é por agenda (client), não por conexão: quem manda nisso
        // é o card da própria Agenda, para onde este botão leva.
        list.push({
          key: `gcal-${c.id}`, icon: 'calendar', title: 'Google Calendar',
          sub: [c.google_email || 'Agenda comercial', clientHint(c, clientId, 'agenda')].filter(Boolean).join(' · '),
          desc: 'Sincroniza os agendamentos da Agenda com o seu Google Calendar.',
          tone: ok ? (unlinked ? 'amber' : 'green') : 'amber',
          statusLabel: ok ? (unlinked ? 'Sem agenda vinculada' : 'Conectado') : 'Reautorizar',
          primary: { label: 'Gerenciar', onClick: () => setManage({ kind: 'gcal', id: c.id }) },
        })
        return
      }
      list.push({
        key: `google-${c.id}`, icon: 'ads', title: 'Google Ads',
        sub: [c.google_email || 'Anúncios', clientHint(c, clientId)].filter(Boolean).join(' · '),
        desc: 'Importe campanhas, métricas e conversões do Google Ads.',
        tone: ok ? (unlinked ? 'amber' : 'green') : 'amber',
        statusLabel: ok ? (unlinked ? 'Sem contas vinculadas' : 'Conectado') : 'Reautorizar',
        primary: { label: 'Gerenciar', onClick: () => setManage({ kind: 'google', id: c.id }) },
      })
    })

    // Meta (org) — um card por conexão, agrupados por produto: Meta Ads, Facebook e Instagram (#320).
    buildMetaCards(visibleConnections(metaConns, clientId)).forEach((card) => {
      const { conn, product, status } = card
      const noPage = card.warnNoFacebook && status.tone === 'green'
      const unlinked = isUnlinked(conn) && status.tone === 'green'
      list.push({
        key: card.key, icon: PRODUCT_INFO[product].icon, title: card.title,
        sub: [card.sub, clientHint(conn, clientId)].filter(Boolean).join(' · '), desc: card.desc,
        tone: noPage || unlinked ? 'amber' : status.tone,
        statusLabel: noPage ? 'Sem Página' : unlinked ? 'Sem contas vinculadas' : status.label,
        primary: { label: 'Gerenciar', onClick: () => setManage({ kind: 'meta', id: conn.id }) },
      })
    })

    // Por cliente selecionado — apenas as que já têm configuração.
    if (canManageClients && selectedClient) {
      const client = selectedClient

      const waState = waStatus[client.id]
      if (waState) {
        const wa = WA_STATUS_MAP[waState] || WA_STATUS_MAP.default
        list.push({
          key: `wa-${client.id}`, icon: 'whatsapp', title: 'WhatsApp', sub: client.name,
          desc: 'Automação de mensagens no WhatsApp via Evolution API.',
          docUrl: 'https://doc.evolution-api.com',
          tone: wa[0], statusLabel: wa[1], checking: waChecking,
          primary: { label: 'Gerenciar', onClick: () => setWaClient(client) },
        })
      }

      const metaAccount = metaByClient[client.id] || null
      if (metaAccount) {
        const m = META_STATUS_MAP[metaAccount.status] || META_STATUS_MAP.default
        list.push({
          key: `wameta-${client.id}`, icon: 'whatsapp', title: 'WhatsApp Oficial', sub: client.name,
          desc: 'WhatsApp Business com coexistência, pela Cloud API oficial da Meta. Independe das conexões do Meta Ads, Facebook e Instagram.',
          tone: m[0], statusLabel: m[1],
          primary: { label: 'Gerenciar', onClick: () => setMetaClient(client) },
        })
      }

      if (client.tracker_key) {
        list.push({
          key: `tracker-${client.id}`, icon: 'key', title: 'Script de rastreio', sub: client.name,
          desc: 'Código de rastreamento instalado no site do cliente.',
          tone: 'green', statusLabel: 'Disponível',
          primary: { label: 'Instalar', onClick: () => setTrackerClient(client) },
        })
      }
    }

    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleConns, metaConns, waStatus, waChecking, metaByClient, selectedClient, canManageClients, authorizing, navigate])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return cards
    return cards.filter((c) =>
      `${c.title} ${c.sub} ${c.desc}`.toLowerCase().includes(q)
    )
  }, [cards, query])

  return (
    <div>
      <div className="conn-toolbar">
        <input
          className="input"
          placeholder="Pesquisar…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="conn-count">
          {loading ? 'Carregando…' : `${filtered.length} resultado(s)`}
        </span>
        <button className="btn btn-black btn-sm conn-create" onClick={() => setCreateOpen(true)}>
          <Icon n="plus" s={14} />
          <span style={{ marginLeft: 6 }}>Criar</span>
        </button>
      </div>

      {!loading && filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-desc">
            Nenhuma integração configurada ainda. Clique em <strong>Criar</strong> para adicionar uma.
          </div>
        </div>
      ) : (
        <div className="int-grid">
          {filtered.map((c) => (
            <IntCard key={c.key} {...c} />
          ))}
        </div>
      )}

      <ConnectionCreateModal
        open={createOpen}
        client={canManageClients ? selectedClient : null}
        onClose={() => setCreateOpen(false)}
        onGoogleAds={connectGoogleAds}
        onMeta={connectMeta}
        onWhatsapp={(client) => { setCreateOpen(false); setWaClient(client) }}
        onWhatsappMeta={(client) => { setCreateOpen(false); setMetaClient(client) }}
        onTracker={(client) => { setCreateOpen(false); setTrackerClient(client) }}
      />

      <WhatsappIntegrationModal
        open={!!waClient}
        client={waClient}
        onClose={() => { setWaClient(null); refreshWaLive() }}
      />
      <MetaIntegrationModal
        open={!!metaClient}
        client={metaClient}
        account={metaClient ? metaByClient[metaClient.id] || null : null}
        onClose={() => { setMetaClient(null); loadMetaAccounts() }}
      />
      <MetaLeadAdsModal open={leadAdsOpen} onClose={() => setLeadAdsOpen(false)} />
      <MetaMessagingModal open={messagingOpen} onClose={() => setMessagingOpen(false)} />
      <ConnectionManageModal
        open={!!managed}
        onClose={() => setManage(null)}
        title={managed?.title}
        subtitle={managed?.subtitle}
        statusLabel={managed?.statusLabel}
        tone={managed?.tone}
        served={managed?.served}
        actions={managed?.actions}
        busy={manageBusy || authorizing}
        onAction={onManageAction}
      />
      <TrackerInstallModal
        open={!!trackerClient}
        client={trackerClient}
        onClose={() => setTrackerClient(null)}
      />
    </div>
  )
}

// Logos das integrações, em public/logos/integracao/.
const INT_LOGOS = {
  ads: 'google_ads.webp',
  meta: 'meta-ads.png',
  facebook: 'facebook.svg',
  instagram: 'instagram.svg',
  whatsapp: 'whatsapp.webp',
  key: 'rastreio.png',
}

function IntLogo({ icon, size }) {
  const file = INT_LOGOS[icon]
  if (file) {
    return (
      <img
        src={`/logos/integracao/${file}`}
        alt=""
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    )
  }
  return <Icon n={icon} s={size} />
}

const WA_STATUS_MAP = {
  open: ['green', 'Conectado'],
  close: ['red', 'Desconectado'],
  connecting: ['amber', 'Aguardando'],
  default: ['neutral', 'Não vinculado'],
}

const META_STATUS_MAP = {
  active: ['green', 'Conectado'],
  revoked: ['red', 'Desconectado'],
  default: ['neutral', 'Não vinculado'],
}

/**
 * Card de integração — segue o mock: status no topo (com link de doc opcional),
 * ícone + título/subtítulo, descrição, divisor e ações no rodapé.
 */
function IntCard({ icon, title, sub, desc, tone, statusLabel, checking, docUrl, primary, secondary }) {
  const host = docUrl ? docUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : null
  return (
    <div className="int-card">
      <div className="int-card-top">
        <span className={`int-status int-status-${tone}${checking ? ' loading-pulse' : ''}`}>
          <span className={`dot dot-${tone === 'neutral' ? 'gray' : tone}`} />
          {statusLabel}
        </span>
        {docUrl && (
          <a className="int-card-doc" href={docUrl} target="_blank" rel="noopener noreferrer">
            {host}
            <Icon n="link" s={12} />
          </a>
        )}
      </div>

      <div className="int-card-head">
        <div className="int-card-icon">
          <IntLogo icon={icon} size={20} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="int-card-title">{title}</div>
          <div className="int-card-sub">{sub}</div>
        </div>
      </div>

      {desc && <div className="int-card-desc">{desc}</div>}

      <div className="int-card-foot">
        {primary && (
          <button
            className="btn btn-outline btn-sm"
            onClick={primary.onClick}
            disabled={primary.disabled}
          >
            <Icon n="settings" s={13} />
            <span style={{ marginLeft: 6 }}>{primary.label}</span>
          </button>
        )}
        {secondary && (
          <button className="btn btn-ghost btn-sm" onClick={secondary.onClick}>
            {secondary.label}
          </button>
        )}
      </div>
    </div>
  )
}

// ── Modal "Criar conexão" — seletor por categoria ───────────────────────────
// Esquerda: categorias. Direita: serviços da categoria. Serviços por cliente
// (WhatsApp, Rastreio) valem para o cliente selecionado no topo do sistema: não há
// seletor aqui (#355). Sem cliente selecionado, ficam desabilitados com um aviso.
const CATEGORIES = [
  { id: 'whatsapp', label: 'WhatsApp', icon: 'whatsapp' },
  { id: 'google', label: 'Google', icon: 'ads' },
  { id: 'meta', label: 'Meta', icon: 'meta' },
  { id: 'rastreio', label: 'Rastreio', icon: 'key' },
]

export function ConnectionCreateModal({ open, client, onClose, onGoogleAds, onMeta, onWhatsapp, onWhatsappMeta, onTracker }) {
  const [cat, setCat] = useState('whatsapp')

  useEffect(() => {
    if (open) setCat('whatsapp')
  }, [open])

  if (!open) return null

  // Categorias por cliente: mostram para qual cliente a conexão vai, ou pedem um cliente no topo.
  const clientNote = client ? (
    <div className="cc-option-sub" style={{ marginBottom: 4 }}>Vale para o cliente <strong>{client.name}</strong>.</div>
  ) : (
    <div className="cc-option-sub" style={{ marginBottom: 4 }}>Selecione um cliente no topo do sistema para criar esta conexão.</div>
  )

  const content = {
    whatsapp: (
      <>
        {clientNote}
        <Option
          icon="whatsapp" title="Evolution API" sub="Automação de mensagens (Evolution)"
          disabled={!client} onClick={() => client && onWhatsapp(client)}
        />
        <Option
          icon="whatsapp" title="API Oficial do WhatsApp (Meta)" sub="WhatsApp Cloud API"
          disabled={!client} onClick={() => client && onWhatsappMeta(client)}
        />
      </>
    ),
    google: (
      <Option icon="ads" title="Google Ads" sub="Importar campanhas do Google Ads" onClick={onGoogleAds} />
    ),
    meta: (
      <>
        <Option icon="meta" title="Meta Ads" sub="Contas de anúncios: campanhas e métricas" onClick={() => onMeta('ads')} />
        <Option icon="facebook" title="Facebook" sub="Páginas, Lead Ads e orgânico da Página" onClick={() => onMeta('facebook')} />
        <Option icon="instagram" title="Instagram" sub="Orgânico: posts, seguidores e comentários" onClick={() => onMeta('instagram')} />
        <Option icon="attend" title="Mensagens" sub="Instagram Direct e Messenger no Atendimento" onClick={() => onMeta('messaging')} />
      </>
    ),
    rastreio: (
      <>
        {clientNote}
        <Option
          icon="key" title="Script de rastreio" sub="Instalar o script de tracking no site"
          disabled={!client} onClick={() => client && onTracker(client)}
        />
      </>
    ),
  }

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal cc-modal" role="dialog" aria-modal="true">
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="modal-title">Criar conexão</span>
          <button className="btn-icon" onClick={onClose} aria-label="Fechar">
            <Icon n="x" s={16} />
          </button>
        </div>

        <div className="cc-body">
          <div className="cc-cats">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`cc-cat${cat === c.id ? ' active' : ''}`}
                onClick={() => setCat(c.id)}
              >
                <span className="sb-item-icon" style={{ display: 'flex' }}><IntLogo icon={c.icon} size={16} /></span>
                {c.label}
              </button>
            ))}
          </div>
          <div className="cc-content">
            {content[cat]}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Opção de serviço no painel direito do modal Criar. */
function Option({ icon, title, sub, onClick, disabled = false, soon = false }) {
  return (
    <button
      type="button"
      className="cc-option"
      onClick={soon ? undefined : onClick}
      disabled={disabled || soon}
      style={soon ? { cursor: 'default' } : undefined}
    >
      <div className="int-card-icon" style={{ width: 40, height: 40, borderRadius: 10 }}>
        <IntLogo icon={icon} size={18} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="cc-option-title">{title}</div>
        <div className="cc-option-sub">{sub}</div>
      </div>
      {soon && <span className="badge badge-neutral">Em breve</span>}
    </button>
  )
}
